import type { CRMField, CRMRecord } from '@/types/saas';
import type { CrmPhoneField } from '@/stores/ui-store';
import { waUserIdFromEmail } from '@/lib/utils/phone';

const PHONE_KEYWORDS = ['phone', 'mobile', 'fax', 'tel'];

/**
 * Extract phone-like fields from a set of CRM records using the module's
 * field schema.
 *
 * Matches a field when its dataType is `'phone'` OR its display label contains
 * one of PHONE_KEYWORDS. Also falls back to any record data key prefixed with
 * `phone_` that isn't already in the schema (used by modules that inject
 * synthetic phone fields dynamically).
 *
 * Previously lived inline inside use-app-init.ts. Extracted so the empty-state
 * retry hook can re-run the same extraction after a delayed re-fetch.
 */
export function extractPhoneFields(
  records: CRMRecord[],
  moduleFields: CRMField[],
): CrmPhoneField[] {
  const phoneFieldDefs = moduleFields.filter((f) => {
    const isPhoneType = f.dataType === 'phone';
    const labelMatch = PHONE_KEYWORDS.some((kw) =>
      f.displayLabel.toLowerCase().includes(kw),
    );
    return isPhoneType || labelMatch;
  });
  console.log('[extractPhoneFields]', {
    recordCount: records.length,
    moduleFieldCount: moduleFields.length,
    phoneFieldDefs: phoneFieldDefs.map((f) => `${f.apiName} (${f.dataType})`),
    sampleRecordKeys: records[0]?.data ? Object.keys(records[0].data) : [],
    samplePhoneValues: phoneFieldDefs.map((f) => `${f.apiName}=${records[0]?.data?.[f.apiName]}`),
  });

  const isMultiRecord = records.length > 1;
  const seenValues = new Set<string>();
  const phoneFields: CrmPhoneField[] = [];
  const schemaFieldNames = new Set(phoneFieldDefs.map((f) => f.apiName));

  for (const record of records) {
    for (const fd of phoneFieldDefs) {
      let rawVal = record.data?.[fd.apiName];
      if (rawVal === undefined || rawVal === null) {
        const dotIdx = fd.apiName.indexOf('.');
        const nestedPath = dotIdx >= 0 ? fd.apiName.substring(dotIdx + 1) : fd.apiName;
        rawVal = nestedPath.split('.').reduce<unknown>(
          (obj, key) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[key] : undefined),
          record.data,
        );
      }
      const rawStr = String(rawVal || '').trim();
      if (!rawStr) continue;
      const vals = rawStr.includes(',') ? rawStr.split(',').map((v) => v.trim()).filter(Boolean) : [rawStr];
      for (let vi = 0; vi < vals.length; vi++) {
        const val = vals[vi];
        const normalized = val.replace(/\D/g, '');
        if (seenValues.has(normalized)) continue;
        seenValues.add(normalized);
        const suffix = vals.length > 1 ? ` ${vi + 1}` : '';
        phoneFields.push({
          apiName: fd.apiName,
          displayLabel: isMultiRecord
            ? `${record.displayName || record.id} — ${fd.displayLabel}${suffix}`
            : `${fd.displayLabel}${suffix}`,
          value: val,
          key: isMultiRecord ? `${record.id}_${fd.apiName}_${vi}` : `${fd.apiName}_${vi}`,
          recordName: isMultiRecord ? (record.displayName || record.id) : undefined,
        });
      }
    }
    if (record.data) {
      for (const key of Object.keys(record.data)) {
        if (!key.startsWith('phone_') || schemaFieldNames.has(key)) continue;
        const rawStr = String(record.data[key] || '').trim();
        if (!rawStr) continue;
        const vals = rawStr.includes(',') ? rawStr.split(',').map((v) => v.trim()).filter(Boolean) : [rawStr];
        for (let vi = 0; vi < vals.length; vi++) {
          const val = vals[vi];
          const normalized = val.replace(/\D/g, '');
          if (seenValues.has(normalized)) continue;
          seenValues.add(normalized);
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          const suffix = vals.length > 1 ? ` ${vi + 1}` : '';
          phoneFields.push({
            apiName: key,
            displayLabel: isMultiRecord
              ? `${record.displayName || record.id} — ${label}${suffix}`
              : `${label}${suffix}`,
            value: val,
            key: isMultiRecord ? `${record.id}_${key}_${vi}` : `${key}_${vi}`,
            recordName: isMultiRecord ? (record.displayName || record.id) : undefined,
          });
        }
      }
    }
  }
  return phoneFields;
}

/**
 * WhatsApp username user ID behind a CRM record, or ''. Username users have no
 * phone, so the server syncs them into the CRM with a dummy email built from
 * their Meta user ID ("US.1349…@wa-user.ulgebra.com") — see waUserIdFromEmail.
 */
export function waUserIdOfRecord(record: CRMRecord): string {
  const email = record.email || (typeof record.data?.Email === 'string' ? record.data.Email : '');
  for (const part of email.split(',')) {
    const waUserId = waUserIdFromEmail(part);
    if (waUserId) return waUserId;
  }
  return '';
}

/**
 * Fallback for the WhatsApp app when a record has no phone fields: offer the
 * username user ID (from the dummy email) as the contact, so the CRM widget can
 * open and send to that user's chat. Returns [] when no record carries one.
 */
export function extractWaUsernameFields(records: CRMRecord[]): CrmPhoneField[] {
  const isMultiRecord = records.length > 1;
  const fields: CrmPhoneField[] = [];
  for (const record of records) {
    const waUserId = waUserIdOfRecord(record);
    if (!waUserId) continue;
    fields.push({
      apiName: 'Email',
      displayLabel: isMultiRecord
        ? `${record.displayName || record.id} — WhatsApp username`
        : 'WhatsApp username',
      value: waUserId,
      key: isMultiRecord ? `${record.id}_Email_wa` : 'Email_wa',
      recordName: isMultiRecord ? (record.displayName || record.id) : undefined,
    });
  }
  return fields;
}
